import { useCallback, useEffect } from 'react';
import { useFloorplanStore } from '@repo/front-end-state';

/**
 * Warn before leaving the wizard with unsaved plan work — and, when the operator cancels, really
 * stay.
 *
 * ## Why it is needed now and was not before
 *
 * The Observers stage's edits used to live in local React state and reach the plan only when Save
 * fired, so an operator who loaded a twin's live configuration, re-drew a region and then clicked
 * the logo lost the lot with no prompt. Since `observers-page-draft` every edit is in the store as
 * it is made and saves itself; `isDirty` now means a draft save is pending or has failed, and the
 * page lets a pending one finish before this guard asks. That was survivable while the stage showed nothing worth losing; it
 * is not, now that a real gym's 150 observers arrive on open and get adjusted in place.
 *
 * ## The predicate is `isDirty` and nothing else
 *
 * `useFloorplanStore.isDirty` existed before this feature and **had no reader anywhere in
 * `apps/twin-viewer`** — the store set it, and nothing asked. Two changes in this feature make it
 * truthful in both directions, and the guard is only as good as they are:
 *
 *  - the Observers stage now calls `setDirty(true)` when an observer is edited (it previously had
 *    no caller at all), and
 *  - the live-configuration load puts the flag **back** where it found it clean, because a load is
 *    not the operator's work. Without that the guard would warn on every single open, which is
 *    exactly the guard operators learn to dismiss — AC10 forbids it in as many words.
 *
 * ## Two halves, because there are two ways out
 *
 * `beforeunload` covers a reload, a closed tab and a typed address; the browser owns that dialog
 * and its wording. `guardLeave()` covers the wizard's own exit, and is the half that can be
 * cancelled meaningfully — the operator stays on the stage with the edit intact.
 *
 * ⚠ **Not React Router's `useBlocker`,** which the spec's Technical Approach named. That hook
 * requires a **data router** (`createBrowserRouter`); this app mounts `<BrowserRouter>`, so calling
 * it throws rather than degrading. The requirement is unaffected — the wizard has exactly one
 * in-app exit reachable with unsaved work, the header's logo button, so guarding that one call site
 * covers the same ground without re-architecting the app's routing.
 */

export interface LeaveGuard {
  /**
   * Ask whether it is safe to leave. Resolves `true` to proceed, `false` to stay put.
   *
   * Resolves `true` immediately when there is nothing unsaved — no dialog, no pause.
   */
  guardLeave: () => Promise<boolean>;
}

export interface UseLeaveGuardParams {
  /**
   * Asks the question: the onboarding Modal's leave prompt (`useOnboardingModal().confirmLeave`),
   * which owns its copy. Resolves `true` only on *Leave and discard*. This hook keeps only WHEN to ask.
   */
  confirmLeave: () => Promise<boolean>;
}

/**
 * The one question the guard asks.
 *
 * Read through `getState()` rather than a subscription on purpose: the `beforeunload` handler is
 * installed once and must see the value at the moment the browser asks, not the value captured when
 * the effect last ran.
 */
const hasUnsavedWork = (): boolean => useFloorplanStore.getState().isDirty;

export const useLeaveGuard = ({ confirmLeave }: UseLeaveGuardParams): LeaveGuard => {
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      if (!hasUnsavedWork()) return;
      /*
       * Both, deliberately. `preventDefault()` is the specified mechanism and what current Chrome
       * and Firefox act on; a non-empty `returnValue` is what older Safari and the legacy path
       * require. Neither browser shows our text — the wording is the browser's — so there is no
       * copy to keep in step here.
       */
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  const guardLeave = useCallback(async (): Promise<boolean> => {
    if (!hasUnsavedWork()) return true;
    return confirmLeave();
  }, [confirmLeave]);

  return { guardLeave };
};
